import { ColumnDef } from "@tanstack/react-table";
import { PlusIcon } from "lucide-react";
import { Business } from "@norma-bi/bi-api";
import { BaseSettingsTable } from "../components/BaseSettingsTable";
import { BaseSettingsCell } from "../components/BaseSettingsCell";
import React from "react";
import { RenameDialog } from "./RenameDialog";
import { useUpdateBusiness } from "@/hooks/useUpdateBusiness";
import { customToast } from "@/utils/customToast";
import { AddBusinessDialog } from "./AddBusinessDialog";
import { useAuth } from "@/Auth";
import { Button } from "@/components/ui/button";
import { SettingsPageHeader } from "../components/SettingsPageHeader";
import { useT } from "@/i18n";

export type SettingsBusinessesTableProps = {
  businesses: Business[];
};

export function SettingsBusinessesTable({ businesses }: SettingsBusinessesTableProps) {
  const [selectedBusiness, setSelectedBusiness] = React.useState<Business | null>(null);
  const [showRenameDialog, setShowRenameDialog] = React.useState(false);
  const [showAddBusinessDialog, setShowAddBusinessDialog] = React.useState(false);

  const updateBusiness = useUpdateBusiness();
  const isDemo = useAuth().user?.is_demo ?? false;
  const { t, formatNumber } = useT();

  const columns: ColumnDef<Business, keyof Business>[] = [
    {
      accessorKey: "display_name",
      header: t("businesses.displayName"),
      cell: ({ getValue }) => BaseSettingsCell.avatar(getValue()),
    },
    {
      accessorKey: "name",
      header: t("businesses.businessName"),
      cell: ({ getValue }) => <span dir="auto">{getValue()}</span>,
    },
    {
      accessorKey: "venues_count",
      header: t("businesses.venues"),
      cell: ({ row }) => (
        <span className="tabular-nums">{formatNumber(row.original.venues_count)}</span>
      ),
    },
    {
      accessorKey: "users_count",
      header: t("common.users"),
      cell: ({ row }) => (
        <span className="tabular-nums">{formatNumber(row.original.users_count)}</span>
      ),
    },
  ];

  const TopSection = (
    <SettingsPageHeader title={t("settings.businesses")}>
      {!isDemo && (
        <Button type="button" onClick={() => setShowAddBusinessDialog(true)}>
          <PlusIcon data-icon="inline-start" />
          {t("businesses.addBusiness")}
        </Button>
      )}
    </SettingsPageHeader>
  );

  const handleRowClick = (business: Business) => {
    setSelectedBusiness(business);
    setShowRenameDialog(true);
  };

  const handleRename = (newDisplayName: string) => {
    if (selectedBusiness) {
      updateBusiness.mutate(
        { businessId: selectedBusiness.id, changeset: { display_name: newDisplayName } },
        {
          onSuccess: () => {
            customToast(t("businesses.renamed"), "success", "bottom");
            setShowRenameDialog(false);
            setSelectedBusiness(null);
          },
          onError: (error) => {
            console.error("Error updating business name:", error);
            customToast(t("businesses.renameFailed"), "error", "bottom");
          },
        },
      );
    }
  };

  return (
    <>
      <BaseSettingsTable
        data={businesses}
        columns={columns}
        TopSection={TopSection}
        onRowClick={isDemo ? undefined : handleRowClick}
      />

      {selectedBusiness && showRenameDialog && (
        <RenameDialog
          show={showRenameDialog}
          setShow={setShowRenameDialog}
          business={selectedBusiness}
          onConfirm={handleRename}
          isConfirmPending={updateBusiness.isPending}
        />
      )}

      {showAddBusinessDialog && (
        <AddBusinessDialog onClose={() => setShowAddBusinessDialog(false)} />
      )}
    </>
  );
}
